/**
 * WorkflowChatService
 *
 * Frontend service for interacting with the workflow chat API.
 * Handles conversation management and message processing.
 */

import axios from 'axios';
import { ExecutionContext } from '@/utils/executionLogFormatter';
import { getWorkflowRequestHeaders } from '@/services/api/workflowApi';
import { WorkflowEdgeSnapshot, WorkflowNodeSnapshot } from '@/types/workflow-editor';

// Configuration
const WORKFLOW_API_BASE_URL = import.meta.env.VITE_WORKFLOW_BACKEND_URL || '/workflow-api';

/**
 * Suggested node from AI
 */
export interface SuggestedNode {
  type: string;
  name: string;
  description?: string;
  config?: Record<string, unknown>;
  positionHint?: 'after' | 'before' | 'parallel';
  /** ID of node to insert after (for precise insertion) */
  insertAfterNodeId?: string;
  /** ID of node to insert before (for precise insertion) */
  insertBeforeNodeId?: string;
}

/**
 * Suggested edge/connection
 */
export interface SuggestedEdge {
  sourceType: string;
  targetType: string;
  sourceHandle?: string;
  targetHandle?: string;
  /** Specific source node ID (for precise edge creation) */
  sourceNodeId?: string;
  /** Specific target node ID (for precise edge creation) - use '__NEW_NODE__' placeholder for new nodes */
  targetNodeId?: string;
}

/**
 * Modification to an existing node (rename, reconfigure)
 */
export interface NodeModification {
  /** ID of the existing node to modify */
  nodeId: string;
  /** New label/display name (optional) */
  newLabel?: string;
  /** Config properties to update (merged with existing) */
  configUpdates?: Record<string, unknown>;
  /** Description of what's being changed (for UI display) */
  changeDescription: string;
}

/**
 * Workflow suggestion from AI
 */
export interface WorkflowSuggestion {
  description: string;
  nodes: SuggestedNode[];
  /** Modifications to existing nodes (rename, update config) */
  nodeModifications?: NodeModification[];
  edges?: SuggestedEdge[];
  /** Edge IDs to remove (when inserting a node between existing nodes) */
  edgesToRemove?: string[];
  isComplete: boolean;
  clarificationQuestion?: string;
  clarificationOptions?: string[];
}

/**
 * Chat message
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  tokens?: number;
  workflowSuggestion?: WorkflowSuggestion;
  suggestionAccepted?: boolean;
}

/**
 * Conversation
 */
export interface Conversation {
  _id: string;
  userId: string;
  orgId: string;
  workflowId?: string;
  title: string;
  messages: ChatMessage[];
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

/**
 * Pagination info
 */
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  pagination?: PaginationInfo;
}

/**
 * WorkflowChatService class
 */
class WorkflowChatService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${WORKFLOW_API_BASE_URL}/api/workflow-chat`;
  }

  /**
   * Build auth headers with validated organization context.
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      return await getWorkflowRequestHeaders();
    } catch (error) {
      console.warn('Failed to get auth headers:', error);
      return {};
    }
  }

  /**
   * List conversations
   */
  async listConversations(params?: {
    workflowId?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Conversation[]>> {
    const response = await axios.get<ApiResponse<Conversation[]>>(
      `${this.baseUrl}/conversations`,
      {
        params,
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Get a conversation by ID
   */
  async getConversation(conversationId: string): Promise<ApiResponse<Conversation>> {
    const response = await axios.get<ApiResponse<Conversation>>(
      `${this.baseUrl}/conversations/${conversationId}`,
      {
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Create a new conversation
   */
  async createConversation(params: {
    title?: string;
    workflowId?: string;
  }): Promise<ApiResponse<Conversation>> {
    const response = await axios.post<ApiResponse<Conversation>>(
      `${this.baseUrl}/conversations`,
      params,
      {
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Send a message to a conversation
   * @param conversationId - The conversation ID
   * @param content - The message content
   * @param options - Optional parameters including execution context for debugging
   */
  async sendMessage(
    conversationId: string,
    content: string,
    options?: {
      /** Execution context for AI-assisted debugging */
      executionContext?: ExecutionContext;
      /** Current nodes in the editor (may differ from saved DB state) */
      currentNodes?: WorkflowNodeSnapshot[];
      /** Current edges in the editor */
      currentEdges?: WorkflowEdgeSnapshot[];
    }
  ): Promise<ApiResponse<{ userMessage: ChatMessage; assistantMessage: ChatMessage }>> {
    const body: Record<string, unknown> = { content };

    // Add execution context if provided
    if (options?.executionContext) {
      body.executionContext = options.executionContext;
    }

    // Send current editor state so backend uses live nodes/edges instead of stale DB data
    if (options?.currentNodes) {
      body.currentNodes = options.currentNodes;
    }
    if (options?.currentEdges) {
      body.currentEdges = options.currentEdges;
    }

    const response = await axios.post<
      ApiResponse<{ userMessage: ChatMessage; assistantMessage: ChatMessage }>
    >(
      `${this.baseUrl}/conversations/${conversationId}/messages`,
      body,
      {
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Update a conversation
   */
  async updateConversation(
    conversationId: string,
    params: {
      title?: string;
      status?: 'active' | 'archived';
    }
  ): Promise<ApiResponse<Conversation>> {
    const response = await axios.patch<ApiResponse<Conversation>>(
      `${this.baseUrl}/conversations/${conversationId}`,
      params,
      {
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<ApiResponse<null>> {
    const response = await axios.delete<ApiResponse<null>>(
      `${this.baseUrl}/conversations/${conversationId}`,
      {
        headers: await this.getAuthHeaders(),
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const workflowChatService = new WorkflowChatService();
